import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { clearVisit, prunePhotos, savePhoto } from '../lib/results-db';
import { newId } from '../lib/uuid';

/**
 * The state a try-on visit carries between screens: which visit this is, and
 * which photo is currently in play.
 *
 * Both live in `sessionStorage` as well as React state. A reload — or an iOS
 * purge of a backgrounded standalone PWA, which is a cold start rather than a
 * resumed session — otherwise loses them, and with them the ability to re-render
 * the results still sitting in IndexedDB.
 *
 * `sessionStorage` rather than `localStorage` on purpose: a visit belongs to one
 * customer, not to the device.
 *
 * ⚠️ **`visitId` is the scope, and `photoKey` is not.** That distinction is the
 * whole model. A retake issues a new `photoKey` inside the *same* visit, so
 * everything already generated stays — which is exactly what the old code could
 * not express, because it treated the photo as the scope and wiped the results
 * whenever it changed. Only `endVisit` clears anything now.
 */

const VISIT_KEY = 'shringaar.visitId';
const PHOTO_KEY = 'shringaar.photoKey';

interface CustomerSessionValue {
  visitId: string | null;
  photoKey: string | null;
  /** True once a photo exists — what gates entry to the try-on screen. */
  hasVisit: boolean;
  /**
   * Records a freshly uploaded photo. Used for both the first capture and a
   * retake; neither clears results.
   *
   * 🔒 Takes the image itself so the on-device copy is filed under the right
   * visit. The caller cannot do this: on a first capture the visit does not
   * exist until this function mints it.
   */
  setPhotoKey: (key: string, image: Blob) => Promise<void>;
  /** Ends the visit: photo, on-device photo blob, and every result. */
  endVisit: () => Promise<void>;
}

const Context = createContext<CustomerSessionValue | null>(null);

function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    /* private mode — in-memory state still works for this session */
  }
}

export function CustomerSessionProvider({ children }: { children: ReactNode }) {
  const [visitId, setVisitId] = useState<string | null>(() => read(VISIT_KEY));
  const [photoKey, setPhotoKeyState] = useState<string | null>(() => read(PHOTO_KEY));

  const setPhotoKey = useCallback(
    async (key: string, image: Blob) => {
      /**
       * A visit is created lazily by the first photo rather than on entering the
       * flow, so a customer who browses and leaves never gets one. On a retake
       * the existing id is reused — that reuse is what keeps the results.
       */
      const nextVisitId = visitId ?? newId();

      /**
       * 🔒 Persist before publishing the key. The try-on screen reads the photo
       * off the device as soon as it sees a new `photoKey`, so writing after
       * would race it into a missing preview on the first render.
       */
      await savePhoto({ photoKey: key, visitId: nextVisitId, image, createdAt: Date.now() });

      write(VISIT_KEY, nextVisitId);
      write(PHOTO_KEY, key);
      setVisitId(nextVisitId);
      setPhotoKeyState(key);

      // 🔒 A visit keeps at most one raw capture on disk. Results generated from
      // the superseded photo are untouched — see `prunePhotos`.
      await prunePhotos(nextVisitId, key);
    },
    [visitId],
  );

  const endVisit = useCallback(async () => {
    write(VISIT_KEY, null);
    write(PHOTO_KEY, null);
    setVisitId(null);
    setPhotoKeyState(null);
    await clearVisit();
  }, []);

  const value = useMemo(
    () => ({
      visitId,
      photoKey,
      hasVisit: photoKey !== null,
      setPhotoKey,
      endVisit,
    }),
    [visitId, photoKey, setPhotoKey, endVisit],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useCustomerSession(): CustomerSessionValue {
  const value = useContext(Context);
  if (!value) throw new Error('useCustomerSession must be used inside CustomerSessionProvider');
  return value;
}
